import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, NavLink } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import type { Message } from "wall/types/message.model";
import type { Event } from "event/types/event.model";
import {
  NAV_CROSSBAR,
  NAV_CLICK_AND_COLLECT,
  NAV_MAN_MATCH,
  NAV_PRONOS,
  NAV_SELFIES_MSG,
  NAV_TITLE,
  NAV_QUESTIONS,
  NAV_QUIZ,
  NAV_BINGO,
  NAV_HTML_PAGE,
  NAV_HTML_PAGE_TWO,
  NAV_PRONOSTICSV2,
  NAV_FOOD_MENU_V2,
} from "_constants";
import "./ListNavigation.css";
import {
  DEFAULT_GOLD_COLOR,
  DEFAULT_PSG_NAVIGATION_MENU_LOGO,
  DEFAULT_WHITE_COLOR,
} from "../../../_graphic_design";

interface NavigationProps {
  event: Event;
  form?: UseFormReturnType<
    Partial<Message>,
    (values: Partial<Message>) => Partial<Message>
  >;
  open: boolean;
  setIsMessageSent?: React.Dispatch<React.SetStateAction<boolean>>;
}

type Nav =
  | "crossbar"
  | "clickandcollect"
  | "default"
  | "motm"
  | "prono"
  | "questions"
  | "quiz"
  | "bingo"
  | "wall"
  | "htmlpage"
  | "htmlpagetwo"
  | "foodmenuv2"
  | "pronosticsv2";
interface State {
  event: Event;
  nav: Nav;
}

export function ListNavigation({
  event,
  form,
  open,
  setIsMessageSent,
}: NavigationProps) {
  const location = useLocation();
  const { state } = location;

  const [isNavOpen, setIsNavOpen] = useState(open);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const navPrefix = useMemo(() => `/event/${event.id}`, [event]);

  function getNavTitle(type: Nav) {
    let fn;
    const navs = {
      clickandcollect: () =>
        event.navigation?.click_and_collect || NAV_CLICK_AND_COLLECT,
      crossbar: () => event.navigation?.crossbar || NAV_CROSSBAR,
      motm: () => event.navigation?.man_of_match || NAV_MAN_MATCH,
      prono: () => event.navigation?.pronostics || NAV_PRONOS,
      questions: () => event.navigation?.questions || NAV_QUESTIONS,
      quiz: () => event.navigation?.quiz || NAV_QUIZ,
      wall: () => event.navigation?.selfies || NAV_SELFIES_MSG,
      bingo: () => event.navigation?.bingo || NAV_BINGO,
      default: () => event.navigation?.title || NAV_TITLE,
      htmlpage: () => event.navigation?.html_page || NAV_HTML_PAGE,
      htmlpagetwo: () => event.navigation?.html_page_two || NAV_HTML_PAGE_TWO,
      foodmenuv2: () => event.navigation?.food_menu_v2 || NAV_FOOD_MENU_V2,
      pronosticsv2: () => event.navigation?.pronostics_v2 || NAV_PRONOSTICSV2,
    };

    if (navs[type]) {
      fn = navs[type];
    } else {
      fn = navs["default"];
    }

    return fn();
  }

  const currentNav: Nav = useMemo(
    () => (state as State)?.nav as Nav | "default",
    [state],
  );

  const enabledNavItems = [
    event.pronostics_display,
    event.selfies_display,
    event.questions_display,
    event.quiz_display,
    event.bingo_game_display,
    event.html_page_display,
    event.html_page_two_display,
    event.pronostics_v2_display,
    event.crossbar_display,
    event.man_of_the_match_display,
    event.food_menu_display,
    event.food_menu_v2_display,
  ].filter(Boolean).length;

  if (enabledNavItems <= 1) {
    return null;
  }

  const psgLogo =
    event?.images.navigationMenuLogo ?? DEFAULT_PSG_NAVIGATION_MENU_LOGO;

  const navigationMenuBackgroundColor =
    event?.colors.navigationMenuBackground ?? DEFAULT_WHITE_COLOR;
  const navigationMenuTextColor =
    event?.colors.navigationMenuText ?? DEFAULT_GOLD_COLOR;

  return (
    <Box
      className="nav-wrapper-list"
      style={{
        color: `${navigationMenuTextColor}`,
        background: `${navigationMenuBackgroundColor}`,
      }}
    >
      <NavLink
        classNames={{
          label: "nav-title-list",
        }}
        label={getNavTitle(currentNav)}
        icon={
          <img
            src={psgLogo}
            alt="Logo du menu déroulant"
            style={{ width: "30px", height: "auto", objectFit: "contain" }}
          />
        }
        opened={isNavOpen}
        onClick={() => toggleNav()}
        style={{
          color: `${navigationMenuTextColor}`,
          background: `${navigationMenuBackgroundColor}`,
        }}
      >
        {event.pronostics_display && currentNav !== "prono" ? (
          <NavLink
            component={Link}
            classNames={{
              label: "nav-item-list",
            }}
            label={event.navigation?.pronostics || NAV_PRONOS}
            state={{ event, nav: "prono" }}
            to={`${navPrefix}/prono`}
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {event.selfies_display && currentNav !== "wall" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.selfies || NAV_SELFIES_MSG}
              </div>
            }
            onClick={() => {
              if (setIsMessageSent && form) {
                setIsMessageSent(false);
                form.setFieldValue("message", "");
              }
              toggleNav();
            }}
            state={{ event, nav: "wall" }}
            to={
              event.wall_id
                ? `${navPrefix}/wall/${event.wall_id}`
                : `${navPrefix}/wall`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {event.questions_display && currentNav !== "questions" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.questions || NAV_QUESTIONS}
              </div>
            }
            onClick={() => {
              if (setIsMessageSent && form) {
                setIsMessageSent(false);
                form.setFieldValue("message", "");
              }
              toggleNav();
            }}
            state={{ event, nav: "questions" }}
            to={
              event.wall_id
                ? `${navPrefix}/wall/${event.wall_questions_id}`
                : `${navPrefix}/wall`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {event.quiz_display && currentNav !== "quiz" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.quiz || NAV_QUIZ}
              </div>
            }
            state={{ event, nav: "quiz" }}
            to={
              event.quiz_id
                ? `${navPrefix}/quiz/${event.quiz_id}`
                : `${navPrefix}/quiz`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}
        {/* BINGO */}
        {event.bingo_game_display && currentNav !== "bingo" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.bingo || NAV_BINGO}
              </div>
            }
            state={{ event, nav: "bingo" }}
            to={
              event.bingo_game_id
                ? `${navPrefix}/bingogame/${event.bingo_game_id}`
                : `${navPrefix}/bingogame`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}
        {/* html page */}
        {event.html_page_display && currentNav !== "htmlpage" ? (
          <NavLink
            component={Link}
            onClick={() => toggleNav()}
            label={
              <div className="nav-item-list">
                {event.navigation?.html_page || NAV_HTML_PAGE}
              </div>
            }
            state={{ event, nav: "htmlpage" }}
            to={
              event.html_page_id
                ? `${navPrefix}/htmlpage/${event.html_page_id}`
                : `${navPrefix}/htmlpage`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}
        {/* html page two*/}
        {event.html_page_two_display && currentNav !== "htmlpagetwo" ? (
          <NavLink
            component={Link}
            onClick={() => toggleNav()}
            label={
              <div className="nav-item-list">
                {event.navigation?.html_page_two || NAV_HTML_PAGE_TWO}
              </div>
            }
            state={{ event, nav: "htmlpagetwo" }}
            to={
              event.html_page_two_id
                ? `${navPrefix}/htmlpage/${event.html_page_two_id}`
                : `${navPrefix}/htmlpage`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {/* pronostucs v2 */}
        {event.pronostics_v2_display && currentNav !== "pronosticsv2" ? (
          <NavLink
            component={Link}
            onClick={() => toggleNav()}
            label={
              <div className="nav-item-list">
                {event.navigation?.pronostics_v2 || NAV_PRONOSTICSV2}
              </div>
            }
            state={{ event, nav: "pronosticsv2" }}
            to={
              event.pronostics_v2_id
                ? `${navPrefix}/pronosticsv2/${event.pronostics_v2_id}`
                : `${navPrefix}/pronosticsv2`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {event.crossbar_display && currentNav !== "crossbar" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.crossbar || NAV_CROSSBAR}
              </div>
            }
            state={{ event, nav: "crossbar" }}
            to={
              event.crossbar_event_id
                ? `${navPrefix}/crossbar/${event.crossbar_event_id}`
                : `${navPrefix}/crossbar`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {event.man_of_the_match_display && currentNav !== "motm" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.man_of_match || NAV_MAN_MATCH}
              </div>
            }
            state={{ event, nav: "motm" }}
            to={`${navPrefix}/motm`}
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}
        {event.food_menu_v2_display && currentNav !== "foodmenuv2" ? (
          <NavLink
            component={Link}
            onClick={() => toggleNav()}
            label={
              <div className="nav-item-list">
                {event.navigation?.food_menu_v2 || NAV_FOOD_MENU_V2}
              </div>
            }
            state={{ event, nav: "foodmenuv2" }}
            to={
              event.html_page_id
                ? `${navPrefix}/foodmenuv2`
                : `${navPrefix}/foodmenuv2`
            }
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}

        {event.food_menu_display && currentNav !== "clickandcollect" ? (
          <NavLink
            component={Link}
            label={
              <div className="nav-item-list">
                {event.navigation?.click_and_collect || NAV_CLICK_AND_COLLECT}
              </div>
            }
            state={{ event, nav: "clickandcollect" }}
            to={`${navPrefix}/clickandcollect`}
            style={{
              color: `${navigationMenuTextColor}`,
              background: `${navigationMenuBackgroundColor}`,
            }}
          />
        ) : null}
      </NavLink>
    </Box>
  );
}
